<template>
  <div :class="$style.wrapper">
    <svg ref="svgElement" :class="$style.svg" viewBox="0 0 100 100">
      <circle
        :cx="HALF"
        :cy="HALF"
        :r="R"
        :class="$style.incompleteCircle"
        :stroke="backgroundColor"
        :stroke-width="strokeWidth"
        fill="transparent"
      />

      <path
        ref="pathElement"
        :d="path"
        :class="$style.completePath"
        :stroke-width="strokeWidth"
        stroke="#9bcbeb"
        fill="transparent"
      />
    </svg>

    <div :class="$style.content">
      <Typography :variant="TYPOGRAPHY_TYPES.bodyBold" :class="$style.unit">
        <slot>
          {{ `${percentage}%` }}
        </slot>
      </Typography>
    </div>
  </div>
</template>

<script>
import { inView } from 'motion'
import { animate } from 'motion/mini'
import { computed, defineComponent, toRefs, ref, onMounted } from 'vue-demi'

import { normalize } from '@front/common/utils'
import { Typography, TYPOGRAPHY_TYPES } from '@front/ui'

import { getPositionFromAngle } from './utils'

export default defineComponent({
  components: { Typography },
  props: {
    percentage: {
      type: Number,
      required: true,
    },
    strokeWidth: {
      type: Number,
      default: 16,
    },
    backgroundColor: {
      type: String,
      default: '#F1F3F5',
    },
    isAnimated: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { percentage, strokeWidth, isAnimated } = toRefs(props)
    const svgElement = ref(null)
    const pathElement = ref(null)

    const HALF = 50
    const R = HALF - strokeWidth.value * 0.5
    const ORIGIN = { x: HALF, y: HALF }

    const startingPoint = getPositionFromAngle(HALF, HALF, R, 0)

    const angle = computed(() => {
      return normalize(percentage.value, [0, 100], [0, 360])
    })

    const path = computed(() => {
      return `
        M ${startingPoint.x} ${startingPoint.y}
        A ${R} ${R} 0 ${angle.value > 180 ? 1 : 0} 1
        ${getPositionFromAngle(ORIGIN.x, ORIGIN.y, R, angle.value).x}
        ${getPositionFromAngle(ORIGIN.x, ORIGIN.y, R, angle.value).y}
      `
    })

    onMounted(() => {
      if (!isAnimated.value) {
        return
      }

      const el = pathElement.value

      if (!el) {
        return
      }

      const length = el.getTotalLength()

      el.style.strokeDasharray = `${length}`
      el.style.strokeDashoffset = `${length}`

      const run = () => {
        animate(
          el,
          { strokeDashoffset: 0 },
          {
            duration: 1.2,
            easing: 'ease-in-out',
          },
        )
      }

      inView(svgElement.value, (element) => run(element), {
        margin: '0px 0px -200px 0px',
      })
    })

    return {
      HALF,
      R,
      path,
      TYPOGRAPHY_TYPES,
      svgElement,
      pathElement,
    }
  },
})
</script>

<style lang="scss" module>
.wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 110px;
  height: 110px;
}

.svg {
  z-index: 0;
  width: 100%;
  transform: rotate(-90deg);
}

.incompleteCircle {
  stroke-linecap: round;
}

.completePath {
  stroke-linecap: round;
  transition: stroke-dashoffset 1s ease;
}

.content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.unit {
  line-height: 1.1;
}
</style>
